import React from 'react';
import { Badge } from '@/components/ui/badge';
import { CriteriaBreakdownTooltip } from './CriteriaBreakdownTooltip';
import { MatchSignal } from '@/types/recommendation';
import { cn } from '@/lib/utils';

interface MatchScoreBadgeProps {
  /** Fallback score, used only when no signals are supplied. */
  softSignalsMet: number;
  /** When provided, the score is counted from these and hovering reveals them. */
  signals?: MatchSignal[];
  /**
   * Number of criteria the score is out of. Only used when no signals are
   * supplied — a request without required skills is scored on utilisation +
   * role only, so it reads "N of 2".
   */
  totalCriteria?: number;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
}

export function MatchScoreBadge({ softSignalsMet, signals, totalCriteria, tooltipSide }: MatchScoreBadgeProps) {
  // The badge and its breakdown must never disagree, so whenever signals are
  // known they are the only source of both numbers: a stored score can predate
  // the dates, resource or skills the signals were just computed for.
  const scored = signals && signals.length > 0;
  const met = scored ? signals.filter(s => s.met).length : softSignalsMet;
  const total = scored ? signals.length : (totalCriteria ?? 3);
  const accent: 'success' | 'warning' | 'destructive' = met >= total ? 'success' : met >= total - 1 ? 'warning' : 'destructive';
  const badge = (
    <Badge
      variant="accent"
      accent={accent}
      className={cn(
        'text-xs font-semibold px-1.5 py-0 leading-5 tabular-nums whitespace-nowrap',
        signals && signals.length > 0 && 'cursor-help',
      )}
    >
      {met} of {total} met
    </Badge>
  );

  if (!signals || signals.length === 0) return badge;

  return (
    <CriteriaBreakdownTooltip softSignalsMet={met} signals={signals} totalCriteria={total} side={tooltipSide}>
      <span className="inline-flex">{badge}</span>
    </CriteriaBreakdownTooltip>
  );
}
